import React, { useState, useEffect, useCallback } from 'react';
import {
  RiBugLine, RiLightbulbLine, RiDeleteBinLine, RiCheckLine,
  RiArrowGoBackLine, RiRefreshLine, RiInboxUnarchiveLine,
} from 'react-icons/ri';
import { C } from '../../data/constants';
import { EmptyState, LoadingScreen } from '../../components/shared/UI';
import {
  getFeedback, setFeedbackStatus, deleteFeedback, clearResolvedFeedback,
} from '../../api/feedback.api';

/* ══════════════════════════════════════════════════════════════
   FEEDBACK

   Bug reports and suggestions from the account page. Two actions that
   matter: mark something resolved once it is dealt with, and clear the
   resolved ones out so the list stays a to-do rather than an archive.
══════════════════════════════════════════════════════════════ */

const FILTERS = [
  { key: 'open',       label: 'Open',        query: { status: 'open' } },
  { key: 'bug',        label: 'Bugs',        query: { type: 'bug' } },
  { key: 'suggestion', label: 'Suggestions', query: { type: 'suggestion' } },
  { key: 'resolved',   label: 'Resolved',    query: { status: 'resolved' } },
  { key: 'all',        label: 'All',         query: {} },
];

const when = iso => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });
};

export default function FeedbackManager() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, open: 0, bugs: 0 });
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const load = useCallback(() => {
    setLoading(true); setError('');
    const query = FILTERS.find(f => f.key === filter)?.query || {};
    getFeedback(query)
      .then(res => { setItems(res.data || []); setCounts(res.counts || {}); })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    setBusyId(item._id);
    try {
      await setFeedbackStatus(item._id, item.status === 'resolved' ? 'open' : 'resolved');
      load();
    } catch (err) { setError(err.message); } finally { setBusyId(null); }
  };

  const remove = async (item) => {
    setBusyId(item._id);
    try { await deleteFeedback(item._id); load(); }
    catch (err) { setError(err.message); } finally { setBusyId(null); }
  };

  const clearResolved = async () => {
    setConfirmClear(false);
    try { await clearResolvedFeedback(); load(); }
    catch (err) { setError(err.message); }
  };

  return (
    <div className="page-container fb-page anim-fade-up">
      <header className="fb-head">
        <div>
          <h1>Feedback</h1>
          <p>
            {counts.open} open · {counts.bugs} of them bugs · {counts.total} in total
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-ghost" onClick={load}><RiRefreshLine size={15} /> Refresh</button>
          <button className="btn-ghost" onClick={() => setConfirmClear(true)}>
            <RiInboxUnarchiveLine size={15} /> Clear resolved
          </button>
        </div>
      </header>

      <div className="fb-filters">
        {FILTERS.map(f => (
          <button
            key={f.key}
            className={`fb-filter${filter === f.key ? ' active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div className="fb-error">{error}</div>}

      {loading ? <LoadingScreen label="Loading feedback" /> : !items.length ? (
        <EmptyState
          icon={RiInboxUnarchiveLine}
          title="Nothing here"
          sub={filter === 'open' ? 'No open reports — everything has been dealt with.' : 'No reports match this filter.'}
        />
      ) : (
        <div className="fb-list">
          {items.map(item => (
            <article key={item._id} className={`ks-card fb-item${item.status === 'resolved' ? ' resolved' : ''}`}>
              <div className="fb-item-head">
                <span className={`fb-tag ${item.type}`}>
                  {item.type === 'bug' ? <RiBugLine size={13} /> : <RiLightbulbLine size={13} />}
                  {item.type === 'bug' ? 'Bug' : 'Suggestion'}
                </span>
                <span className="fb-who">
                  {item.name || 'Someone'}
                  {item.email && <em>{item.email}</em>}
                </span>
                <span className="fb-when">{when(item.createdAt)}</span>
              </div>

              <p className="fb-message">{item.message}</p>

              <div className="fb-item-foot">
                <span className="fb-meta">
                  {item.page && <code>{item.page}</code>}
                  {item.userAgent && <small title={item.userAgent}>{item.userAgent.slice(0, 60)}…</small>}
                </span>
                <span className="fb-item-actions">
                  <button className="btn-ghost" disabled={busyId === item._id} onClick={() => toggle(item)}>
                    {item.status === 'resolved'
                      ? <><RiArrowGoBackLine size={14} /> Reopen</>
                      : <><RiCheckLine size={14} /> Resolved</>}
                  </button>
                  <button
                    className="btn-ghost fb-delete"
                    disabled={busyId === item._id}
                    onClick={() => remove(item)}
                    title="Delete this report"
                  >
                    <RiDeleteBinLine size={14} />
                  </button>
                </span>
              </div>
            </article>
          ))}
        </div>
      )}

      {confirmClear && (
        <div className="quit-overlay" role="dialog" aria-modal="true" onClick={() => setConfirmClear(false)}>
          <div className="quit-box anim-pop" onClick={e => e.stopPropagation()}>
            <div className="quit-icon" style={{ color: C.accent }}><RiInboxUnarchiveLine size={22} /></div>
            <h3>Clear resolved feedback?</h3>
            <p>
              This deletes every report already marked resolved. Open reports are
              not touched. It cannot be undone.
            </p>
            <div className="quit-actions">
              <button className="btn-primary" onClick={() => setConfirmClear(false)}>Keep them</button>
              <button className="btn-ghost quit-leave" onClick={clearResolved}>Delete resolved</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
